import { useRef, useState } from "react";
import { toast } from "sonner";
import { Paperclip, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { CATEGORIES } from "@/data/products";

const inputCls =
  "w-full rounded-sm border border-border bg-white px-3 py-2.5 text-sm text-foreground outline-none transition-colors duration-200 placeholder:text-muted-foreground focus:border-indigo focus:ring-1 focus:ring-indigo";

const EMPTY = {
  name: "", company: "", country: "", email: "", whatsapp: "",
  product: "", quantity: "", specifications: "", destination: "", message: "",
  company_website: "", // honeypot
};

const VARIANTS = {
  domestic: { country: "India", destLabel: "Delivery city / state", destPlaceholder: "e.g. Mumbai, Maharashtra" },
  export: { country: "", destLabel: "Destination country / port", destPlaceholder: "e.g. Jebel Ali, UAE" },
  quote: { country: "", destLabel: "Delivery destination", destPlaceholder: "City / country" },
  general: { country: "", destLabel: "Delivery destination (optional)", destPlaceholder: "City / country" },
};

export function EnquiryForm({ variant = "general", title = "Send your enquiry", subtitle, defaultProduct = "" }) {
  const v = VARIANTS[variant] || VARIANTS.general;
  const [form, setForm] = useState({ ...EMPTY, country: v.country, product: defaultProduct });
  const [file, setFile] = useState(null);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [attachmentSent, setAttachmentSent] = useState(false);
  const fileRef = useRef(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) {
      toast.error("File too large — maximum 10 MB.");
      e.target.value = "";
      return;
    }
    setFile(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await api.post("/enquiries", { ...form, enquiry_type: variant });
      if (file && data.id && data.id !== "ok") {
        try {
          const fd = new FormData();
          fd.append("file", file);
          await api.post(`/enquiries/${data.id}/attachment`, fd);
          setAttachmentSent(true);
        } catch (err) {
          toast.error(`Enquiry received, but the file upload failed: ${formatApiError(err)}`);
        }
      }
      setDone(true);
      setForm({ ...EMPTY, country: v.country, product: defaultProduct });
      setFile(null);
      toast.success("Enquiry received. Our team will get back to you.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  if (done) {
    return (
      <div className="rounded-sm border border-gold/50 bg-gold/10 p-10 text-center" data-testid={`enquiry-success-${variant}`}>
        <h3 className="font-display text-2xl font-semibold text-indigo">Thank you — enquiry received.</h3>
        <p className="mt-3 text-sm text-foreground/70">
          Our team reviews every requirement personally and will respond on your email or WhatsApp.
        </p>
        {attachmentSent && (
          <p className="mt-2 text-sm font-medium text-[#178a45]">Your tech pack file was attached successfully.</p>
        )}
        <button
          onClick={() => setDone(false)}
          data-testid={`enquiry-again-${variant}`}
          className="mt-6 rounded-sm border border-indigo px-5 py-2.5 text-sm font-medium text-indigo transition-colors hover:bg-indigo hover:text-white"
        >
          Send another enquiry
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="rounded-sm border border-border bg-white p-6 sm:p-10" data-testid={`enquiry-form-${variant}`}>
      <h2 className="font-display text-2xl font-semibold text-indigo sm:text-3xl">{title}</h2>
      {subtitle && <p className="mt-2 text-sm text-foreground/70">{subtitle}</p>}

      {/* Honeypot — hidden from humans, bots fill it */}
      <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", height: 0, overflow: "hidden" }}>
        <label>
          Company website
          <input type="text" tabIndex={-1} autoComplete="off" value={form.company_website} onChange={set("company_website")} />
        </label>
      </div>

      <div className="mt-8 grid gap-5 sm:grid-cols-2">
        <div>
          <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-foreground/70" htmlFor={`${variant}-name`}>Your name *</label>
          <input id={`${variant}-name`} data-testid={`enquiry-name-${variant}`} required minLength={2} className={inputCls} value={form.name} onChange={set("name")} placeholder="Full name" />
        </div>
        <div>
          <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-foreground/70" htmlFor={`${variant}-company`}>Company</label>
          <input id={`${variant}-company`} data-testid={`enquiry-company-${variant}`} className={inputCls} value={form.company} onChange={set("company")} placeholder="Business / brand name" />
        </div>
        <div>
          <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-foreground/70" htmlFor={`${variant}-country`}>Country *</label>
          <input id={`${variant}-country`} data-testid={`enquiry-country-${variant}`} required minLength={2} className={inputCls} value={form.country} onChange={set("country")} placeholder="Country" />
        </div>
        <div>
          <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-foreground/70" htmlFor={`${variant}-email`}>Email *</label>
          <input id={`${variant}-email`} data-testid={`enquiry-email-${variant}`} required type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@company.com" />
        </div>
        <div>
          <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-foreground/70" htmlFor={`${variant}-whatsapp`}>WhatsApp number *</label>
          <input id={`${variant}-whatsapp`} data-testid={`enquiry-whatsapp-${variant}`} required minLength={6} className={inputCls} value={form.whatsapp} onChange={set("whatsapp")} placeholder="With country code" />
        </div>
        <div>
          <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-foreground/70" htmlFor={`${variant}-product`}>Product *</label>
          <select id={`${variant}-product`} data-testid={`enquiry-product-${variant}`} required className={inputCls} value={form.product} onChange={set("product")}>
            <option value="" disabled>Select a range</option>
            {CATEGORIES.map((c) => (
              <option key={c.id} value={c.label}>{c.label}</option>
            ))}
            <option value="Other / multiple ranges">Other / multiple ranges</option>
          </select>
        </div>
        <div>
          <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-foreground/70" htmlFor={`${variant}-quantity`}>Estimated quantity *</label>
          <input id={`${variant}-quantity`} data-testid={`enquiry-quantity-${variant}`} required className={inputCls} value={form.quantity} onChange={set("quantity")} placeholder="e.g. 500 pieces per style" />
        </div>
        <div>
          <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-foreground/70" htmlFor={`${variant}-destination`}>{v.destLabel}</label>
          <input id={`${variant}-destination`} data-testid={`enquiry-destination-${variant}`} className={inputCls} value={form.destination} onChange={set("destination")} placeholder={v.destPlaceholder} />
        </div>
        <div className="sm:col-span-2">
          <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-foreground/70" htmlFor={`${variant}-specs`}>Specifications</label>
          <textarea id={`${variant}-specs`} data-testid={`enquiry-specifications-${variant}`} rows={3} className={inputCls} value={form.specifications} onChange={set("specifications")} placeholder="Fabric, fit, wash, sizes, labelling — or attach a tech pack when we connect" />
        </div>
        <div className="sm:col-span-2">
          <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-foreground/70">Attach tech pack (optional)</label>
          <input
            ref={fileRef}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,.webp,.zip"
            className="hidden"
            data-testid={`enquiry-file-${variant}`}
            onChange={pickFile}
          />
          {file ? (
            <div className="flex items-center justify-between rounded-sm border border-gold/50 bg-gold/10 px-3 py-2.5" data-testid={`enquiry-file-chosen-${variant}`}>
              <span className="flex items-center gap-2 text-sm text-indigo">
                <Paperclip className="h-4 w-4 text-gold-dark" />
                {file.name} <span className="text-xs text-muted-foreground">({(file.size / 1024 / 1024).toFixed(1)} MB)</span>
              </span>
              <button type="button" onClick={() => { setFile(null); if (fileRef.current) fileRef.current.value = ""; }}
                aria-label="Remove file" data-testid={`enquiry-file-remove-${variant}`}
                className="text-muted-foreground transition-colors hover:text-[#B42318]">
                <X className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              data-testid={`enquiry-file-button-${variant}`}
              className="flex w-full items-center justify-center gap-2 rounded-sm border border-dashed border-border bg-white px-3 py-2.5 text-sm text-foreground/60 transition-colors hover:border-indigo hover:text-indigo"
            >
              <Paperclip className="h-4 w-4" /> PDF, JPG, PNG or ZIP — up to 10 MB
            </button>
          )}
        </div>
        <div className="sm:col-span-2">
          <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-foreground/70" htmlFor={`${variant}-message`}>Message</label>
          <textarea id={`${variant}-message`} data-testid={`enquiry-message-${variant}`} rows={3} className={inputCls} value={form.message} onChange={set("message")} placeholder="Anything else we should know" />
        </div>
      </div>

      <button
        type="submit"
        disabled={sending}
        data-testid={`enquiry-submit-${variant}`}
        className="mt-8 w-full rounded-sm bg-indigo px-6 py-3.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-indigo-deep disabled:opacity-60 sm:w-auto sm:px-12"
      >
        {sending ? "Sending…" : "Submit enquiry"}
      </button>
      <p className="mt-3 text-xs text-muted-foreground">No retail orders. B2B and bulk enquiries only.</p>
    </form>
  );
}
